// shared-lib/group-customization/transforms.js
//
// Pure frame-math primitives used by the group-editor link system. Anchor's
// local frame has the anchor at origin with its rotation.y aligned with
// world +X. Y rotation follows the right-hand rule (positive = CCW looking
// down +Y). dy is rotation-invariant.

/**
 * Convert a world-space point to a frame anchored at `anchor.center` with
 * its X axis rotated by `anchor.rotation.y`.
 *
 * @param {{x:number, y:number, z:number}} point
 * @param {{center:{x:number,y:number,z:number}, rotation?:{y?:number}}} anchor
 * @returns {{dx:number, dy:number, dz:number}}
 */
function worldToLocal(point, anchor) {
  if (!point || !anchor?.center) return { dx: 0, dy: 0, dz: 0 };
  const rotY = anchor.rotation?.y || 0;
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);
  const diffX = (point.x ?? 0) - (anchor.center.x ?? 0);
  const diffZ = (point.z ?? 0) - (anchor.center.z ?? 0);
  const diffY = (point.y ?? 0) - (anchor.center.y ?? 0);
  return {
    dx: diffX * cos - diffZ * sin,
    dy: diffY,
    dz: diffX * sin + diffZ * cos,
  };
}

/**
 * Inverse of `worldToLocal`.
 *
 * @param {{dx:number, dy:number, dz:number}} offset
 * @param {{center:{x:number,y:number,z:number}, rotation?:{y?:number}}} anchor
 * @returns {{x:number, y:number, z:number}}
 */
function localToWorld(offset, anchor) {
  if (!offset || !anchor?.center) return { x: 0, y: 0, z: 0 };
  const rotY = anchor.rotation?.y || 0;
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);
  return {
    x: (anchor.center.x ?? 0) + (offset.dx || 0) * cos + (offset.dz || 0) * sin,
    y: (anchor.center.y ?? 0) + (offset.dy || 0),
    z: (anchor.center.z ?? 0) - (offset.dx || 0) * sin + (offset.dz || 0) * cos,
  };
}

/**
 * Rotate a 3-component offset by Y angle θ from a child frame into its
 * parent frame. Same convention as `localToWorld` (R(θ) around +Y).
 */
function rotateLocalToAnchor(offset, theta) {
  if (!offset) return { dx: 0, dy: 0, dz: 0 };
  const c = Math.cos(theta || 0);
  const s = Math.sin(theta || 0);
  return {
    dx: (offset.dx || 0) * c + (offset.dz || 0) * s,
    dy: offset.dy || 0,
    dz: -(offset.dx || 0) * s + (offset.dz || 0) * c,
  };
}

/**
 * Inverse of `rotateLocalToAnchor`.
 */
function rotateAnchorToLocal(offset, theta) {
  if (!offset) return { dx: 0, dy: 0, dz: 0 };
  const c = Math.cos(theta || 0);
  const s = Math.sin(theta || 0);
  return {
    dx: (offset.dx || 0) * c - (offset.dz || 0) * s,
    dy: offset.dy || 0,
    dz: (offset.dx || 0) * s + (offset.dz || 0) * c,
  };
}

/**
 * Theta defining the frame `link.offset` is stored in.
 *   'copy' (default) — source-local frame; theta = source.rotYRel so the
 *     offset rotates with the source.
 *   'none' — anchor frame; theta = 0 so the offset is invariant under
 *     source rotation.
 * Used by both write (computeRpOffset) and read (constraintSolver) so a
 * single rotation call handles both modes.
 */
function rpOffsetFrameTheta(rotationMode, source) {
  return (rotationMode || 'copy') === 'copy' ? source?.rotYRel || 0 : 0;
}

/**
 * Compute the offset that puts `target` at its current position relative to
 * `source`, in the frame implied by `rotationMode` (see rpOffsetFrameTheta).
 */
function computeRpOffset(source, target, rotationMode) {
  if (!source || !target) return { dx: 0, dy: 0, dz: 0 };
  const anchor = {
    dx: (target.dx || 0) - (source.dx || 0),
    dy: (target.dy || 0) - (source.dy || 0),
    dz: (target.dz || 0) - (source.dz || 0),
  };
  return rotateAnchorToLocal(anchor, rpOffsetFrameTheta(rotationMode, source));
}

/**
 * Y-rotation delta the target keeps relative to the source (anchor-local
 * frame). Stored on RP links so source rotation propagates to the target.
 */
function computeRpRotYDiff(source, target) {
  if (!source || !target) return 0;
  return (target.rotYRel || 0) - (source.rotYRel || 0);
}

/** Initial center-line position: midpoint between the two objects' dx. */
function computeCenterLineX(a, b) {
  return ((a?.dx || 0) + (b?.dx || 0)) / 2;
}

module.exports = {
  worldToLocal,
  localToWorld,
  rotateLocalToAnchor,
  rotateAnchorToLocal,
  rpOffsetFrameTheta,
  computeRpOffset,
  computeRpRotYDiff,
  computeCenterLineX,
};
